import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';
import { PRODUCTS_KEY } from '../api';

type Update<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Update'];

/**
 * ניהול מוצרים ו"הכר את המוצר" (PRD 5.7).
 *
 * הכתיבה למוצרים ישירה, כי ה RLS מגביל אותה ל products.manage. הפרסום
 * עצמו עובר ב RPC: הוא כותב הודעה לערוץ, שלעתים הוא "הנהלה בלבד",
 * והקישור בין ההודעה לפרסום חייב לקרות באותה עסקה.
 */

export type AdminSpotlight = {
  id: string;
  product_id: string;
  quiz_id: string | null;
  channel_id: string | null;
  message_id: string | null;
  headline: string | null;
  due_date: string | null;
  published_at: string | null;
  products: { name: string } | null;
  channels: { name: string } | null;
  quizzes: { title: string } | null;
};

export type SpotlightReport = {
  published_at: string | null;
  has_quiz: boolean;
  total: number;
  seen: number;
  passed: number;
  employees: {
    employee_id: string;
    name: string;
    branch: string | null;
    seen: boolean;
    best_score: number | null;
    passed: boolean;
  }[];
};

const ADMIN_PRODUCTS_KEY = ['admin', 'products'] as const;
const SPOTLIGHTS_KEY = ['admin', 'spotlights'] as const;
const CATEGORIES_KEY = ['admin', 'product-categories'] as const;

export type ProductSearchRow = {
  id: string;
  name: string;
  name_en: string | null;
  sku: string | null;
  image_path: string | null;
  is_active: boolean;
  categories: { id: string; name: string }[];
};

export type ProductSearchResult = { total: number; rows: ProductSearchRow[] };

export const PRODUCT_PAGE_SIZE = 50;

/**
 * חיפוש בקטלוג, בשרת (מיגרציה 089).
 *
 * placeholderData שומר את העמוד הקודם על המסך בזמן שההקלדה הבאה
 * נטענת. בלעדיו כל תו מרוקן את הטבלה לשלד ומחזיר אותה, והמסך
 * מהבהב בכל הקלדה.
 */
export function useProductSearch(input: {
  term: string;
  categoryId: string | null;
  includeInactive: boolean;
  page: number;
}) {
  return useQuery({
    queryKey: [...ADMIN_PRODUCTS_KEY, input],
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<ProductSearchResult> => {
      const { data, error } = await supabase.rpc('product_search', {
        p_term: input.term.trim() || undefined,
        p_category: input.categoryId ?? undefined,
        p_include_inactive: input.includeInactive,
        p_limit: PRODUCT_PAGE_SIZE,
        p_offset: input.page * PRODUCT_PAGE_SIZE,
      });
      if (error) throw error;
      return (data ?? { total: 0, rows: [] }) as unknown as ProductSearchResult;
    },
  });
}

export type ProductDraft = {
  id: string | null;
  name: string;
  name_en: string;
  sku: string;
  description: string;
  selling_points: string;
  image_path: string | null;
  launched_at: string;
  is_active: boolean;
  category_ids: string[];
};

export function emptyProduct(): ProductDraft {
  return {
    id: null,
    name: '',
    name_en: '',
    sku: '',
    description: '',
    selling_points: '',
    image_path: null,
    launched_at: '',
    is_active: true,
    category_ids: [],
  };
}

/** המוצר המלא לעריכה. התיאור ונקודות המכירה אינם ברשימה, רק כאן. */
export function useProductForEdit(productId: string | null) {
  return useQuery({
    queryKey: [...ADMIN_PRODUCTS_KEY, 'edit', productId],
    enabled: Boolean(productId),
    queryFn: async (): Promise<ProductDraft | null> => {
      const { data, error } = await supabase.rpc('product_for_edit', {
        p_product: productId as string,
      });
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as {
        id: string;
        name: string;
        name_en: string | null;
        sku: string | null;
        description: string | null;
        selling_points: string | null;
        image_path: string | null;
        launched_at: string | null;
        is_active: boolean;
        category_ids: string[];
      };
      return {
        id: row.id,
        name: row.name,
        name_en: row.name_en ?? '',
        sku: row.sku ?? '',
        description: row.description ?? '',
        selling_points: row.selling_points ?? '',
        image_path: row.image_path,
        launched_at: row.launched_at ?? '',
        is_active: row.is_active,
        category_ids: row.category_ids ?? [],
      };
    },
  });
}

/** המחלקות, עם מספר המוצרים בכל אחת. */
export function useProductCategories() {
  return useQuery({
    queryKey: CATEGORIES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_categories')
        .select('id, name, sort_order, product_category_links ( count )')
        .order('sort_order')
        .order('name');
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        sort_order: row.sort_order,
        product_count:
          (row.product_category_links as unknown as { count: number }[])?.[0]?.count ?? 0,
      }));
    },
  });
}

export function useCategoryMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
    void queryClient.invalidateQueries({ queryKey: ADMIN_PRODUCTS_KEY });
    void queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'product-lists'] });
  };

  const create = useMutation({
    mutationFn: async (name: string) => {
      const { error } = await supabase.from('product_categories').insert({ name });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const rename = useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      const { error } = await supabase
        .from('product_categories')
        .update({ name: input.name })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('product_categories').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /**
   * פתיחת מחלקה מתוך טופס המוצר (מיגרציה 090).
   *
   * המקום שבו מגלים שחסרה מחלקה הוא כשמוסיפים מוצר ואין לאן
   * לשייך אותו, ומסע למסך אחר באמצע טופס פתוח מאבד את הטופס.
   */
  const add = useMutation({
    mutationFn: async (name: string): Promise<string> => {
      const { data, error } = await supabase.rpc('add_product_category', {
        p_name: name.trim(),
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: refresh,
  });

  /** הוספה והסרה של מוצרים במחלקה אחת, מכיוון המחלקה. */
  const setProducts = useMutation({
    mutationFn: async (input: { categoryId: string; add?: string[]; remove?: string[] }) => {
      const { error } = await supabase.rpc('set_category_products', {
        p_category: input.categoryId,
        p_add: input.add ?? [],
        p_remove: input.remove ?? [],
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { create, add, rename, remove, setProducts };
}

export function useSpotlights() {
  return useQuery({
    queryKey: SPOTLIGHTS_KEY,
    queryFn: async (): Promise<AdminSpotlight[]> => {
      const { data, error } = await supabase
        .from('product_spotlights')
        .select(
          `id, product_id, quiz_id, channel_id, message_id, headline, due_date, published_at,
           products ( name ), channels ( name ), quizzes ( title )`,
        )
        .order('published_at', { ascending: false, nullsFirst: true });
      if (error) throw error;
      return (data ?? []) as unknown as AdminSpotlight[];
    },
  });
}

export function useSpotlightReport(spotlightId: string | null) {
  return useQuery({
    queryKey: ['admin', 'spotlight-report', spotlightId],
    enabled: Boolean(spotlightId),
    queryFn: async (): Promise<SpotlightReport | null> => {
      const { data, error } = await supabase.rpc('spotlight_report', {
        p_spotlight: spotlightId as string,
      });
      if (error) throw error;
      return data as SpotlightReport | null;
    },
  });
}

/** הרשימות שהטפסים בוחרים מהן. */
export function useProductLists() {
  return useQuery({
    queryKey: ['admin', 'product-lists'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const [categories, channels] = await Promise.all([
        supabase.from('product_categories').select('id, name').order('sort_order'),
        supabase
          .from('channels')
          .select('id, name')
          .eq('is_archived', false)
          .order('name'),
      ]);
      if (categories.error) throw categories.error;
      if (channels.error) throw channels.error;
      return { categories: categories.data ?? [], channels: channels.data ?? [] };
    },
  });
}

export function useProductMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_PRODUCTS_KEY });
    void queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY });
    // גם המחלקות: המונה שליד כל מחלקה משתנה בכל שמירת מוצר
    void queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
  };

  /** המוצר והמחלקות שלו נשמרים יחד (מיגרציה 089), ולא בשתי פעולות. */
  const save = useMutation({
    mutationFn: async (draft: ProductDraft): Promise<string> => {
      const { data, error } = await supabase.rpc('save_product', {
        // הטיפוסים הנוצרים מצהירים על p_id כמחרוזת, ו null שם הוא
        // "מוצר חדש" — ערך חוקי שאין לו ביטוי בטיפוס
        p_id: draft.id as unknown as string,
        p_name: draft.name.trim(),
        p_name_en: draft.name_en.trim(),
        p_sku: draft.sku.trim(),
        p_description: draft.description.trim(),
        p_selling_points: draft.selling_points.trim(),
        p_image_path: draft.image_path ?? '',
        p_launched_at: draft.launched_at || (null as unknown as string),
        p_is_active: draft.is_active,
        p_category_ids: draft.category_ids,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { save, remove };
}

export function useSpotlightMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: SPOTLIGHTS_KEY });
    void queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['messages'] });
  };

  const create = useMutation({
    mutationFn: async (input: {
      product_id: string;
      channel_id: string | null;
      headline: string | null;
      due_date: string | null;
    }): Promise<string> => {
      const { data, error } = await supabase
        .from('product_spotlights')
        .insert(input)
        .select('id')
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: async (input: { id: string; patch: Update<'product_spotlights'> }) => {
      const { error } = await supabase
        .from('product_spotlights')
        .update(input.patch)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const publish = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('publish_spotlight', { p_spotlight: id });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('product_spotlights').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /** חידון קצר למוצר. 3 עד 5 שאלות לפי ה PRD, ולכן ציון עובר נמוך. */
  const createQuiz = useMutation({
    mutationFn: async (input: { spotlightId: string; title: string }): Promise<string> => {
      const { data, error } = await supabase
        .from('quizzes')
        .insert({ title: input.title, pass_score: 60 })
        .select('id')
        .single();
      if (error) throw error;

      const linked = await supabase
        .from('product_spotlights')
        .update({ quiz_id: data.id })
        .eq('id', input.spotlightId);
      if (linked.error) throw linked.error;

      return data.id;
    },
    onSuccess: refresh,
  });

  return { create, update, publish, remove, createQuiz };
}
